import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-800 mb-4">漫画未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  const statusColor = comic.status === '连载中' ? 'bg-accent-500' : 'bg-secondary-500';

  return (
    <>
      <SEO
        title={`${comic.title} - ${comic.author} - 韩漫网站免费阅读`}
        description={`韩漫网站提供${comic.title}免费在线阅读，${comic.description.substring(0, 120)}。高品质韩漫，舒适阅读体验。`}
        keywords={`${comic.title},${comic.author},韩漫,韩国漫画,${comic.category},免费阅读`}
        canonicalUrl={`https://www.manhwa-site.com/comic/${slug}`}
        ogType="article"
        ogImage={comic.cover}
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <article className="py-6">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[5/7] rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${comic.author}韩国漫画`}
                    title={`${comic.title} - ${comic.author}作品`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading">{comic.title}</h1>
                  <span className={`${statusColor} text-background-50 text-xs px-2.5 py-0.5 rounded-full font-medium whitespace-nowrap`}>
                    {comic.status}
                  </span>
                </div>

                <p className="text-sm text-foreground-600 mb-4">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <span className="flex items-center text-sm text-primary-500 font-medium">
                    <i className="ri-star-fill mr-1"></i>
                    {comic.rating} 分
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-file-list-3-line mr-1"></i>
                    共 {comic.chapters.length} 话
                  </span>
                  <span className="text-sm text-foreground-500">
                    更新于 {comic.updatedAt}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-0.5 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100/60 rounded-lg p-4 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer">
                    开始阅读 第1话
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-1"></i>收藏
                  </button>
                </div>
              </div>
            </div>
          </article>

          <section className="pb-8" aria-labelledby="chapters-title">
            <div className="flex items-center justify-between mb-4">
              <h2 id="chapters-title" className="text-xl font-bold text-foreground-800 font-heading">章节列表</h2>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-500">共 {comic.chapters.length} 话</span>
                <button className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                  <i className="ri-sort-desc mr-1"></i>倒序
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {comic.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/chapter/${comic.slug}/${chapter.id}`}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-background-100 border border-background-200/70 transition-all duration-200 cursor-pointer"
                >
                  <span className="text-sm text-foreground-700 font-medium whitespace-nowrap">{chapter.title}</span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.updatedAt}</span>
                </Link>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="pb-14" aria-labelledby="related-title">
              <h2 id="related-title" className="text-xl font-bold text-foreground-800 font-heading mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': `${comic.title} - ${comic.author}`,
          'description': comic.description,
          'author': { '@type': 'Person', 'name': comic.author },
          'image': comic.cover,
          'datePublished': comic.chapters[0]?.updatedAt || comic.updatedAt,
          'dateModified': comic.updatedAt,
          'publisher': { '@type': 'Organization', 'name': '韩漫网站', 'url': 'https://www.manhwa-site.com' },
          'url': `https://www.manhwa-site.com/comic/${slug}`,
        })}} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.manhwa-site.com/' },
            { '@type': 'ListItem', 'position': 2, 'name': comic.category, 'item': `https://www.manhwa-site.com/category/${comic.categorySlug}` },
            { '@type': 'ListItem', 'position': 3, 'name': comic.title, 'item': `https://www.manhwa-site.com/comic/${slug}` },
          ],
        })}} />
      </main>

      <Footer />
    </>
  );
}